/* Because — OpenAI Ads attribution: click id (oppref) + browser ref (obref)
   captured on the storefront and written onto the cart, so they land on the order.

   WHY THIS LIVES IN THE THEME
   ---------------------------
   docs/openai-ads-custom-pixel.js used to POST the click id to /cart/update.js
   from inside the pixel itself, and not one order ever came out carrying it. A
   custom pixel runs in Shopify's sandbox — a separate browsing context whose
   fetch is not the shopper's storefront session — so that write never reached
   the cart the shopper checks out with. The storefront can do it, and provably
   does: the "Fit test — …" attributes assets/trial-lp.js writes the same way are
   on live orders today. Verified against production on 2026-08-03: a
   /cart/update.js write persists on an EMPTY cart, survives the trial LP's
   /cart/clear -> /cart/add hop, and is still on the cart that goes to checkout.

   WHAT IT DOES
   ------------
   1. ?oppref=<click id> on any landing URL -> first-party __oppref cookie, 30
      days. Same name and lifetime the oaiq SDK uses, so the SDK keeps finding it
      on later pages: its own window.location is the sandbox iframe, which never
      carries the query param, so the cookie is the only path that works.
   2. __obref — the opaque browser ref the SDK mints for itself — is READ here,
      never generated. It only has meaning if it matches what the SDK sent.
   3. Both go onto the cart as oai_oppref / oai_obref, which is what puts them on
      the order for a server-side leg to pick up.

   CONSENT
   -------
   Skipped only when the Customer Privacy API says this visitor may not be
   tracked — the same idiom as trial-lp.js and defer-third-party.liquid (absent
   API means no consent framework on the page, which is the normal US case).
   Shopify already blocks the OpenAI pixel outright for a refusing visitor (its
   purposes are ANALYTICS + MARKETING), so this keeps the two in step. */
(function () {
  'use strict';

  var PARAM = 'oppref';
  var CLICK_COOKIE = '__oppref';
  var BROWSER_COOKIE = '__obref';
  var CLICK_ATTRIBUTE = 'oai_oppref';
  var BROWSER_ATTRIBUTE = 'oai_obref';
  var MAX_AGE = 60 * 60 * 24 * 30; // 30 days — the SDK's own cookie lifetime
  /* Both values are opaque tokens. Anything outside this set is not one of ours
     and must never reach a Set-Cookie string or a cart attribute. Extended to
     include = for base64 padding (base64url can end with = or ==). */
  var TOKEN = /^[A-Za-z0-9._~=-]{1,256}$/;
  var SESSION_KEY = '_oai_attr';
  var SESSION_ATTEMPT_KEY = '_oai_attr_attempts';

  function trackingAllowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.userCanBeTracked !== 'function') return true;
    try {
      return !!cp.userCanBeTracked();
    } catch (e) {
      return true;
    }
  }

  function readCookie(name) {
    try {
      var match = String(document.cookie || '').match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      if (!match) return undefined;
      var value = decodeURIComponent(match[1]);
      return TOKEN.test(value) ? value : undefined;
    } catch (e) {
      return undefined;
    }
  }

  /* becausemarket.com from www.becausemarket.com, so the cookie survives a
     hostname change between the landing page and the rest of the funnel. */
  function registrableDomain() {
    var host = String(window.location.hostname || '').toLowerCase();
    if (!host || host === 'localhost' || /^[\d.]+$/.test(host)) return undefined;
    var labels = host.split('.').filter(Boolean);
    return labels.length >= 2 ? labels.slice(-2).join('.') : undefined;
  }

  function writeCookie(name, value) {
    var base =
      name +
      '=' +
      encodeURIComponent(value) +
      '; Path=/; Max-Age=' +
      MAX_AGE +
      '; SameSite=Lax' +
      (window.location.protocol === 'https:' ? '; Secure' : '');
    /* Host-only first because it always succeeds; the Domain variant additionally
       covers an apex <-> www hop and is simply ignored if the browser rejects it
       (e.g. on *.myshopify.com, which is a public suffix). */
    var domain = registrableDomain();
    try {
      document.cookie = base;
      if (domain) document.cookie = base + '; Domain=' + domain;
    } catch (e) {
      /* cookies unavailable — nothing to recover, the funnel is unaffected */
    }
  }

  function clickIdFromUrl() {
    try {
      var match = String(window.location.search || '').match(new RegExp('[?&]' + PARAM + '=([^&#]+)'));
      if (!match) return undefined;
      var value = decodeURIComponent(match[1]);
      return TOKEN.test(value) ? value : undefined;
    } catch (e) {
      return undefined;
    }
  }

  /* The attributes this visitor should carry, or {} for the overwhelming majority
     who never came from a ChatGPT ad. Exported so the trial LP can fold them into
     the write it already makes on the way to checkout. */
  function cartAttributes() {
    var attrs = {};
    if (!trackingAllowed()) return attrs;
    var clickId = readCookie(CLICK_COOKIE);
    var browserRef = readCookie(BROWSER_COOKIE);
    if (clickId) attrs[CLICK_ATTRIBUTE] = clickId;
    if (browserRef) attrs[BROWSER_ATTRIBUTE] = browserRef;
    return attrs;
  }

  var inFlight = null;
  var MAX_RETRIES = 3;
  var RETRY_DELAYS = [500, 1000, 2000]; // ms, exponential backoff
  var MAX_SESSION_ATTEMPTS = 6; // cap total attempts per session (~2 page views' worth)

  /* Verify the attributes actually persisted by reading the cart back.
     Returns a promise that resolves to true if all expected attrs are present. */
  function verifyCartAttributes(expectedAttrs) {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (response) {
        if (!response || !response.ok) return false;
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.attributes) return false;
        /* Check that every expected attribute is present with the correct value */
        for (var key in expectedAttrs) {
          if (
            Object.prototype.hasOwnProperty.call(expectedAttrs, key) &&
            cart.attributes[key] !== expectedAttrs[key]
          ) {
            return false;
          }
        }
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  /* Write attributes to cart with read-back verification and retry.
     This guards against race conditions with other scripts (Intelligems, Coframe)
     that also mutate the cart around page load. */
  function writeAndVerifyCartAttributes(attrs, attempt) {
    if (attempt === undefined) attempt = 0;

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
      keepalive: true
    })
      .then(function (response) {
        if (!response || !response.ok) {
          return Promise.reject(new Error('Cart update failed'));
        }
        /* Immediately verify the write actually stuck */
        return verifyCartAttributes(attrs);
      })
      .then(function (verified) {
        if (verified) {
          return true; // success
        }
        /* Not verified — retry if we have attempts left */
        if (attempt < MAX_RETRIES - 1) {
          var delay = RETRY_DELAYS[attempt] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(writeAndVerifyCartAttributes(attrs, attempt + 1));
            }, delay);
          });
        }
        /* Out of retries — give up but don't break the page */
        return false;
      })
      .catch(function () {
        /* Network error or other failure — retry if we have attempts left */
        if (attempt < MAX_RETRIES - 1) {
          var delay = RETRY_DELAYS[attempt] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(writeAndVerifyCartAttributes(attrs, attempt + 1));
            }, delay);
          });
        }
        return false;
      });
  }

  function sync() {
    var attrs = cartAttributes();
    var keys = Object.keys(attrs);
    if (!keys.length) return;

    /* One write per session per value set — same dedupe shape as cc-session.js.
       The value set is part of the key on purpose: __obref is minted by the SDK
       inside the pixel sandbox and usually does not exist yet on the first page,
       so when it appears the key changes and the cart is updated once more. */
    var mark = keys
      .map(function (key) {
        return key + '=' + attrs[key];
      })
      .join('&');
    if (mark === inFlight) return;
    try {
      if (window.sessionStorage) {
        /* Already succeeded for this mark — don't retry */
        if (sessionStorage.getItem(SESSION_KEY) === mark) return;
        
        /* Per-session attempt cap: if this mark keeps failing, stop after
           MAX_SESSION_ATTEMPTS to prevent infinite retry loops across page views */
        var attemptKey = SESSION_ATTEMPT_KEY + ':' + mark;
        var attempts = parseInt(sessionStorage.getItem(attemptKey) || '0', 10);
        if (attempts >= MAX_SESSION_ATTEMPTS) return;
        sessionStorage.setItem(attemptKey, String(attempts + 1));
      }
    } catch (e) {
      /* private mode / storage disabled — fall through and just write */
    }

    /* Write with verification and retry. This is attribution, not checkout: it
       must never block or break a cart. The session mark is only set once the
       write is both sent AND verified, so a failed write is retried on the next
       page instead of being lost. */
    inFlight = mark;
    writeAndVerifyCartAttributes(attrs)
      .then(function (success) {
        inFlight = null;
        if (!success) return;
        /* Only mark as done after successful verification */
        try {
          if (window.sessionStorage) {
            sessionStorage.setItem(SESSION_KEY, mark);
            /* Clear the attempt counter on success */
            sessionStorage.removeItem(SESSION_ATTEMPT_KEY + ':' + mark);
          }
        } catch (e) {
          /* nothing to do — worst case the next page writes again */
        }
      })
      .catch(function () {
        inFlight = null;
      });
  }

  /* Read once, up front: after a consent banner resolves the shopper may already
     have navigated away from the URL that carried the click id. */
  var urlClickId = clickIdFromUrl();

  function run() {
    if (!trackingAllowed()) return;
    if (urlClickId) writeCookie(CLICK_COOKIE, urlClickId);
    sync();
  }

  window.oaiAttribution = { cartAttributes: cartAttributes, sync: sync };

  run();
  /* Second pass: the SDK loads asynchronously inside the pixel sandbox, so its
     __obref cookie normally appears a beat after this script runs. */
  setTimeout(run, 4000);
  try {
    document.addEventListener('visitorConsentCollected', run);
  } catch (e) {
    /* no consent framework on this page */
  }
})();
